import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SidebarMenu } from "./SidebarMenu";
import { useTheme } from "../hooks/useTheme";

interface TitleBarProps {
    title: string;
    className?: string;
    badgeMobile?: number;
}

export const TitleBar: React.FC<TitleBarProps> = ({ title, className, badgeMobile }) => {
    const [isOpen, setIsOpen] = useState(false);
    const {isMobile} = useTheme();
    
  // Toggle menu
  const toggleMenu = () => setIsOpen((prev) => !prev);

  // Close menu when clicking outside
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (event.target instanceof Element && !event.target.closest(".menu-container")) {
      setIsOpen(false);
    }
  }, []);

  // Close menu on ESC key
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  }, []);

  // Attach & remove event listeners when menu opens/closes
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleEscapeKey);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, handleOutsideClick, handleEscapeKey]);

  return (
    <div className={`relative ${className}`}>
      {/* Toggle Button - Move to left */}
      <div className="flex flex-row items-center justify-start gap-2">
        {
          isMobile && (
            <button className="flex justify-center items-center p-2 focus:outline-none" onClick={toggleMenu}>
              {isOpen ? <span className="material-symbols-outlined">close</span> : <span className="material-symbols-outlined">menu</span>}
            </button>
          )
        }
        <h1 className="text-3xl font-bold">{title}</h1>
        {
          isMobile && (badgeMobile ?? 0) > 0 &&(
            <div className="min-h-[24px] min-w-[24px] px-1.5 bg-brand-red rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {badgeMobile}
            </div>
          )
        }
      </div>
      

      {/* Sidebar Menu - Change position and animation */}
      <motion.div
        initial={{ x: "-100%" }}  // Start from left
        animate={{ x: isOpen ? 0 : "-100%" }}  // Slide in from left
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-0 left-0 w-64 h-full bg-brand-bg-1 p-6 flex flex-col items-center z-50 shadow-lg menu-container"
      >
        {/* Close button - Move to right side */}
        <button className="absolute top-5 right-5" onClick={toggleMenu}>
          <span className="material-symbols-outlined">close</span>
        </button>

        {
          isMobile && <SidebarMenu />
        }
      </motion.div>

      {/* Overlay */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black opacity-50 z-40"
          onClick={toggleMenu}
        />
      )}
    </div>
  );
}